import { c as _c } from "react/compiler-runtime";
import { Avatar, Box, HStack, Text } from '@chakra-ui/react';
import { SlateEditor } from '@src/components/sco/RichMarkdownInput';
import { Mentionable } from '@src/components/sco/RichMarkdownInput/plugins/mentions/index';
import useUserMentionOptions from '@src/components/sco/utilities/useUserMentionOptions';
import { ScoUserMentionOption } from '@src/graphql-types';
import { useWindow } from '@src/hooks/useWindow';
import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Range, Transforms } from 'slate';
import { ReactEditor, useFocused, useSlate } from 'slate-react';
export type MentionState = {
  selectedMentionable?: Mentionable;
  selectedIndex?: number;
  search?: string;
  target: Range;
};
export type MentionSelectorProps = {
  mentionState: MentionState;
  setMentionState: Dispatch<SetStateAction<MentionState>>;
  position?: 'top' | 'bottom' | 'floating';
};

/**
 * Place the dropdown at the caret. A plain module function because the React Compiler (1.0.0)
 * cannot compile a component whose try/catch holds optional chaining.
 */
function positionMentionSelector(editor: SlateEditor, target: Range, element: HTMLDivElement | null, position: MentionSelectorProps['position'], win: Window) {
  try {
    const domRange = ReactEditor.toDOMRange(editor, target);
    const cursorRect = domRange.getBoundingClientRect();
    const dropdownRect = element?.getBoundingClientRect();
    if (cursorRect && dropdownRect && element) {
      if (position === 'top') {
        element.style.top = `${cursorRect.top - dropdownRect.height}px`;
      }
      if (position === 'floating') {
        element.style.top = `${cursorRect.top + win.pageYOffset + cursorRect.height}px`;
      }
      if (position !== 'bottom') {
        element.style.left = `${cursorRect.left}px`;
      }

      // We start at -1 then increase so it can position first, prevent flying across the page
      element.style.zIndex = '99999';
    }
  } catch (err) {
    console.log(err);
  }
}
export const MentionSelector = props => {
  const $ = _c(35);
  const window = useWindow();
  const editor = useSlate() as SlateEditor;
  const focused = useFocused();
  const mentionsSelectorRef = useRef(null);
  const position = props.position || "floating";
  const {
    target,
    selectedIndex,
    search
  } = props.mentionState;
  let t0;
  if ($[0] !== props) {
    t0 = target_0 => props.setMentionState(prevState => ({
      ...prevState,
      target: target_0
    }));
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setTarget = t0;
  const t1 = search || "";
  let t2;
  if ($[2] !== t1) {
    t2 = {
      fullName: t1,
      pageSize: 10
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    mentionables
  } = useUserMentionOptions(t2);
  let t3;
  if ($[4] !== props) {
    t3 = mentionable => props.setMentionState(prevState_0 => ({
      ...prevState_0,
      selectedMentionable: mentionable
    }));
    $[4] = props;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const setSelectedMentionable = t3;
  let t4;
  if ($[6] !== mentionables || $[7] !== props.mentionState.selectedIndex || $[8] !== setSelectedMentionable || $[9] !== target) {
    t4 = () => {
      setSelectedMentionable(target ? mentionables[props.mentionState.selectedIndex] : null);
    };
    $[6] = mentionables;
    $[7] = props.mentionState.selectedIndex;
    $[8] = setSelectedMentionable;
    $[9] = target;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = mentionables[props.mentionState.selectedIndex];
  let t6;
  if ($[11] !== mentionables.length || $[12] !== t5) {
    t6 = [mentionables.length, t5];
    $[11] = mentionables.length;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  useEffect(t4, t6);
  let t7;
  if ($[14] !== editor || $[15] !== focused || $[16] !== mentionables.length || $[17] !== position || $[18] !== target || $[19] !== window) {
    t7 = () => {
      if (editor && focused && target && mentionables.length) {
        positionMentionSelector(editor, target, mentionsSelectorRef.current, position, window);
      }
    };
    $[14] = editor;
    $[15] = focused;
    $[16] = mentionables.length;
    $[17] = position;
    $[18] = target;
    $[19] = window;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== editor || $[22] !== mentionables.length || $[23] !== position || $[24] !== target) {
    t8 = [editor, target, mentionables.length, position];
    $[21] = editor;
    $[22] = mentionables.length;
    $[23] = position;
    $[24] = target;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  useEffect(t7, t8);
  let t9;
  if ($[26] !== editor || $[27] !== mentionables || $[28] !== position || $[29] !== selectedIndex || $[30] !== setTarget || $[31] !== target) {
    t9 = target && <Box ref={mentionsSelectorRef} backdropFilter="blur(20px)" backgroundColor="rgba(0,0,0,.9)" borderRadius="6px" boxShadow="0 1px 5px rgba(0,0,0,.2)" data-cy="mentions-portal" left={position === "bottom" ? null : "0"} padding="3px" position={position === "bottom" ? null : "absolute"} top={position === "bottom" ? null : "0"} zIndex={-1}>{mentionables.length === 0 && <Text>No Matches</Text>}{mentionables.map((mentionable_0, i) => <HStack key={mentionable_0.identifier} align="center" background={i === selectedIndex ? "rgba(255,255,255,.25)" : "transparent"} borderRadius="4px" cursor="pointer" gap="8px" justify="flex-start" padding="6px 12px" _hover={{
        background: "rgba(255,255,255,.25)"
      }} onClick={event => {
        event.preventDefault();
        Transforms.select(editor, target);
        editor.insertMention(editor, mentionable_0);
        setTarget(null);
      }}><Avatar.Root size="xs"><Avatar.Fallback name={mentionable_0.label} /><Avatar.Image src={mentionable_0.pictureUrl} /></Avatar.Root><div>{mentionable_0.label}</div></HStack>)}</Box>;
    $[26] = editor;
    $[27] = mentionables;
    $[28] = position;
    $[29] = selectedIndex;
    $[30] = setTarget;
    $[31] = target;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== t9) {
    t10 = <MentionSelectorPortal>{t9}</MentionSelectorPortal>;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
};
export const MentionSelectorPortal = ({
  children
}) => {
  return typeof document === 'object' ? ReactDOM.createPortal(children, document.body) : null;
};